import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/contexts/UnifiedAuthContext";
import { useNotifications } from "@/contexts/NotificationContext";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { ProcessedDocument, TranscriptSegment } from "@/types";
import type { Tag } from "@/integrations/supabase/types";

type Record = Database["public"]["Tables"]["records"]["Row"];
type Template = Database["public"]["Tables"]["templates"]["Row"];
type ProcessingStatus = Database["public"]["Enums"]["processing_status"];

interface RecordWithTemplate extends Record {
  template: Template | null;
}

export interface RecordDetails {
  id: string;
  name: string;
  filePath: string;
  status: string;
  duration: unknown;
  createdAt: string;
  userId: string;
  templateId: string | null;
  processingStatus: ProcessingStatus | null;
  processingStartedAt: string | null;
  processingFinishedAt: string | null;
  processingTime: unknown | null;
  processingResults: Record["processing_results"];
  transcriptText: string | null;
  postProcessingText: string | null;
  tags: Tag[];
  isPublished?: boolean; // Published status
  shareUrl?: string; // Share URL if published
  template?: {
    id: string;
    name: string;
    description: string;
    prompt: string;
    isSystem: boolean | null;
    createdAt: string;
    userId: string | null;
    isDeleted?: boolean; // Признак что шаблон удален
  };
}

export const useRecordDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { addNotification } = useNotifications();

  const [record, setRecord] = useState<RecordDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<ProcessedDocument[]>([]);
  const [transcript, setTranscript] = useState<TranscriptSegment[]>([]);

  const fetchFileDetails = async () => {
    if (!id || !user) return;

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("records")
        .select(
          `
          *,
          template:templates!left(*),
          record_tags(
            tag:tags(*)
          ),
          shared_links!left(id, token, is_active)
        `
        )
        .eq("id", id)
        .eq("user_id", user.id)
        .is("deleted_at", null) // Проверяем, что запись не удалена
        .single();

      if (error) throw error;

      if (data) {
        const record = data as RecordWithTemplate & {
          record_tags: Array<{ tag: Tag }>;
          shared_links?: Array<{ id: string; token: string; is_active: boolean }>;
        };

        // Extract shared links information
        const activeSharedLink = record.shared_links?.find(link => link.is_active);
        const isPublished = !!activeSharedLink;
        const shareUrl = activeSharedLink
          ? `${window.location.origin}/shared/${activeSharedLink.token}`
          : undefined;

        const recordData: RecordDetails = {
          id: record.id,
          name: record.name,
          filePath: record.file_path,
          status: record.status,
          duration: record.duration,
          createdAt: record.created_at,
          userId: record.user_id,
          templateId: record.template_id,
          processingStatus: record.processing_status,
          processingStartedAt: record.processing_started_at,
          processingFinishedAt: record.processing_finished_at,
          processingTime: record.processing_time,
          processingResults: record.processing_results,
          transcriptText: record.transcript_text,
          postProcessingText: record.post_processing_text,
          tags: record.record_tags?.map(rt => rt.tag).filter(Boolean) || [],
          isPublished, // Include published status
          shareUrl, // Include share URL if published
          template: record.template
            ? {
              id: record.template.id,
              name: record.template.name,
              description: record.template.description,
              prompt: record.template.prompt,
              isSystem: record.template.is_system,
              createdAt: record.template.created_at,
              userId: record.template.user_id,
              isDeleted: !!record.template.deleted_at,
            }
            : undefined,
        };

        setRecord(recordData);

        if (record.processing_results) {
          try {
            const processedDocs = processResults(record.processing_results);
            setDocuments(processedDocs);
          } catch (e) {
            console.error("Error processing results:", e);
          }
        }

        if (record.transcript_text) {
          try {
            const segments = processTranscript(record.transcript_text);
            setTranscript(segments);
          } catch (e) {
            console.error("Error processing transcript:", e);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching file details:", error);
      addNotification({
        userId: user.id,
        type: "error",
        message: "Не удалось загрузить информацию о файле",
      });
    } finally {
      setLoading(false);
    }
  };

  // Функция для принудительной перезагрузки данных
  const refetchRecord = async () => {
    await fetchFileDetails();
  };

  useEffect(() => {
    fetchFileDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id]);

  const processResults = (results: Record["processing_results"]): ProcessedDocument[] => {
    if (!results || typeof results !== "object") return [];
    // Логика обработки результатов
    return [];
  };

  const processTranscript = (transcriptText: string): TranscriptSegment[] => {
    try {
      // Логика обработки транскрипции
      return [];
    } catch {
      return [];
    }
  };

  const reprocessFile = async () => {
    if (!record?.id || !user?.id) return;

    try {
      const { error } = await supabase
        .from("records")
        .update({
          processing_status: "queued",
          processing_started_at: null,
          processing_finished_at: null,
          processing_results: null,
          transcript_text: null,
          post_processing_text: null,
        })
        .eq("id", record.id)
        .eq("user_id", user.id);

      if (error) throw error;

      addNotification({
        userId: user.id,
        type: "success",
        message: "Файл отправлен на повторную обработку",
      });
    } catch (error) {
      console.error("Error reprocessing file:", error);
      addNotification({
        userId: user.id,
        type: "error",
        message: "Ошибка при отправке файла на повторную обработку",
      });
    }
  };

  const updateProcessedText = (newProcessedText: string) => {
    if (record) {
      setRecord({
        ...record,
        postProcessingText: newProcessedText,
      });
    }
  };

  return {
    record,
    loading,
    documents,
    transcript,
    reprocessFile,
    updateProcessedText,
    refetchRecord,
  };
};
